import {agendaDirectory} from "../access/patientDirectory";
import {
    collection,
    onSnapshot
} from "../access/firestore";

import {
    useEffect,
    useMemo,
    useState
} from "react";

import {
    db
} from "../firebase";

import {
    useClinic
} from "../context/ClinicContext";

import {
    CalendarDays,
    ChevronLeft,
    ChevronRight,
    Clock3,
    UserRound,
    X,
    CalendarCheck2,
    CalendarRange,
    Calendar1,
    CalendarPlus,
    Pencil
} from "lucide-react";

import CitaForm from "../CitaForm";
import {
    getDeviceTimeZone,
    localizeScheduleEntry
} from "../utils/timezone";


export default function MobileCalendar() {

    const {
        clinicId
    } = useClinic();

    const deviceTimeZone = getDeviceTimeZone();


    const [
        citas,
        setCitas
    ] = useState([]);


    const [
        vista,
        setVista
    ] = useState("hoy");


    const [
        fechaActual,
        setFechaActual
    ] = useState(
        new Date()
    );

    const [
        ahora,
        setAhora
    ] = useState(
        () => new Date()
    );


    const [
        citaSeleccionada,
        setCitaSeleccionada
    ] = useState(null);

    const [
        mostrarCitaForm,
        setMostrarCitaForm
    ] = useState(false);    

    useEffect(() => {

        const actualizarReloj = () =>
            setAhora(
                new Date()
            );

        actualizarReloj();

        const intervalo =
            window.setInterval(
                actualizarReloj,
                30 * 1000
            );

        return () =>
            window.clearInterval(
                intervalo
            );

    }, []);


    // =========================
    // CARGAR CITAS
    // =========================

    useEffect(() => {

        if (!clinicId)
            return;

        return onSnapshot(

            agendaDirectory(db,clinicId),

            snapshot => {

                const data =
                    snapshot.docs
                        .map(doc => {

                            const datos =
                                doc.data();

                            return localizeScheduleEntry(
                                {
                                    id: doc.id,
                                    ...datos,
                                    hora:
                                        normalizarHora(
                                            datos.hora
                                        )
                                },
                                deviceTimeZone
                            );

                        })
                        .sort(
                            (a, b) => {

                                const fechaA =
                                    obtenerFechaCita(
                                        a
                                    );

                                const fechaB =
                                    obtenerFechaCita(
                                        b
                                    );

                                return (
                                    fechaA -
                                    fechaB
                                );

                            }
                        );

                setCitas(
                    data
                );

            }

        );

    }, [clinicId, deviceTimeZone]);


    // =========================
    // FECHA ACTUAL
    // =========================

    const hoy =
        convertirFecha(
            ahora
        );


    const fechaKey =
        convertirFecha(
            fechaActual
        );


    // =========================
    // CITAS HOY
    // =========================

    const citasHoy =
        useMemo(
            () =>
                ordenarCitas(
                    citas.filter(
                        cita =>
                            cita.fecha ===
                            hoy
                    )
                ),
            [
                citas,
                hoy
            ]
        );


    // =========================
    // SEMANA ACTUAL
    // =========================

    const inicioSemana =
        obtenerInicioSemana(
            fechaActual
        );


    const diasSemana =
        Array.from(
            {
                length: 7
            },
            (_, indice) => {

                const fecha =
                    new Date(
                        inicioSemana
                    );

                fecha.setDate(
                    fecha.getDate() +
                    indice
                );

                return fecha;

            }
        );


    // =========================
    // MES ACTUAL
    // =========================

    const diasMes =
        useMemo(
            () =>
                construirCalendarioMes(
                    fechaActual
                ),
            [
                fechaActual
            ]
        );


    // =========================
    // CITAS MES
    // =========================

    const citasMes =
        useMemo(
            () =>
                citas.filter(
                    cita =>
                        cita.fecha &&
                        cita.fecha.startsWith(
                            `${fechaActual.getFullYear()}-${String(
                                fechaActual.getMonth() + 1
                            ).padStart(2, "0")}`
                        )
                ),
            [
                citas,
                fechaActual
            ]
        );


    // =========================
    // NAVEGACIÓN
    // =========================

    const avanzar =
        () => {

            const nuevaFecha =
                new Date(
                    fechaActual
                );


            if (
                vista === "hoy"
            ) {

                nuevaFecha.setDate(
                    nuevaFecha.getDate() +
                    1
                );

            }


            if (
                vista === "semana"
            ) {

                nuevaFecha.setDate(
                    nuevaFecha.getDate() +
                    7
                );

            }


            if (
                vista === "mes"
            ) {

                nuevaFecha.setMonth(
                    nuevaFecha.getMonth() +
                    1
                );

            }


            if (
                vista === "año"
            ) {

                nuevaFecha.setFullYear(
                    nuevaFecha.getFullYear() +
                    1
                );

            }


            setFechaActual(
                nuevaFecha
            );

        };


    const retroceder =
        () => {

            const nuevaFecha =
                new Date(
                    fechaActual
                );


            if (
                vista === "hoy"
            ) {

                nuevaFecha.setDate(
                    nuevaFecha.getDate() -
                    1
                );

            }


            if (
                vista === "semana"
            ) {

                nuevaFecha.setDate(
                    nuevaFecha.getDate() -
                    7
                );

            }


            if (
                vista === "mes"
            ) {

                nuevaFecha.setMonth(
                    nuevaFecha.getMonth() -
                    1
                );

            }


            if (
                vista === "año"
            ) {

                nuevaFecha.setFullYear(
                    nuevaFecha.getFullYear() -
                    1
                );

            }


            setFechaActual(
                nuevaFecha
            );

        };


    const irAHoy =
        () => {

            setFechaActual(
                new Date()
            );

            setVista(
                "hoy"
            );

        };


    // =========================
    // CAMBIAR VISTA
    // =========================

    const cambiarVista =
        nuevaVista => {

            setVista(
                nuevaVista
            );

        };


    // =========================
    // RENDER
    // =========================

    return (

        <div className="vd-mobile-calendar"
            style={{

                minHeight:
                    "100%",

                padding:
                    "20px 16px 110px",

                background:
                    "linear-gradient(180deg, #f8fafc 0%, #eef4ff 100%)",

                boxSizing:
                    "border-box"

            }}
        >

            {/* =========================
                HEADER
            ========================= */}

            <div
                style={{

                    marginBottom:
                        18

                }}
            >

                <div
                    style={{

                        display:
                            "flex",

                        alignItems:
                            "center",

                        justifyContent:
                            "space-between"

                    }}
                >

                    <div>

                        <div
                            style={{

                                fontSize:
                                    13,

                                fontWeight:
                                    700,

                                color:
                                    "#2563eb",

                                textTransform:
                                    "uppercase",

                                letterSpacing:
                                    "0.04em"

                            }}
                        >

                            Tu agenda

                        </div>


                        <h1
                            style={{

                                margin:
                                    "4px 0 0",

                                fontSize:
                                    30,

                                lineHeight:
                                    1.1,

                                color:
                                    "#0f172a"

                            }}
                        >

                            Citas

                        </h1>

                    </div>


                    <div
                        style={{

                            width:
                                52,

                            height:
                                52,

                            borderRadius:
                                18,

                            background:
                                "#eff6ff",

                            display:
                                "flex",

                            alignItems:
                                "center",

                            justifyContent:
                                "center"

                        }}
                    >

                        <CalendarDays
                            size={28}
                            color="#2563eb"
                        />

                    </div>

                </div>


                <div
                    style={{

                        color:
                            "#64748b",

                        fontSize:
                            14,

                        marginTop:
                            7

                    }}
                >

                    Consulta y organiza toda tu agenda.

                </div>

            </div>

            <button
                type="button"
                onClick={() =>
                    setMostrarCitaForm(true)
                }
                style={{
                    width:
                        "100%",

                    marginTop:
                        14,

                    marginBottom:
                        12,

                    border:
                        "none",

                    borderRadius:
                        16,

                    background:
                        "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",

                    color:
                        "#ffffff",

                    padding:
                        "13px 16px",

                    display:
                        "flex",

                    alignItems:
                        "center",

                    justifyContent:
                        "center",

                    gap:
                        8,

                    fontSize:
                        14,

                    fontWeight:
                        800,

                    boxShadow:
                        "0 8px 20px rgba(37,99,235,0.22)",

                    cursor:
                        "pointer"
                }}
            >
                <CalendarPlus
                    size={19}
                />

                Agendar nueva cita

            </button>


            {/* =========================
                RESUMEN
            ========================= */}

            <div
                style={{

                    display:
                        "grid",

                    gridTemplateColumns:
                        "repeat(3, 1fr)",

                    gap:
                        10,

                    marginBottom:
                        18

                }}
            >

                <ResumenCard
                    numero={
                        citasHoy.length
                    }
                    texto="Hoy"
                />


                <ResumenCard
                    numero={
                        citas.filter(
                            cita =>
                                cita.fecha >
                                hoy
                        ).length
                    }
                    texto="Próximas"
                />


                <ResumenCard
                    numero={
                        citas.filter(
                            cita =>
                                cita.fecha <
                                hoy
                        ).length
                    }
                    texto="Pasadas"
                />

            </div>


            {/* =========================
                SELECTOR DE VISTAS
            ========================= */}

            <div
                style={{

                    background:
                        "#ffffff",

                    border:
                        "1px solid #e2e8f0",

                    borderRadius:
                        18,

                    padding:
                        5,

                    display:
                        "grid",

                    gridTemplateColumns:
                        "repeat(4, 1fr)",

                    gap:
                        4,

                    marginBottom:
                        14,

                    boxShadow:
                        "0 5px 18px rgba(15,23,42,0.05)"

                }}
            >

                <VistaButton
                    activo={
                        vista === "hoy"
                    }
                    texto="Hoy"
                    icono={
                        <CalendarCheck2
                            size={16}
                        />
                    }
                    onClick={() =>
                        cambiarVista(
                            "hoy"
                        )
                    }
                />


                <VistaButton
                    activo={
                        vista === "semana"
                    }
                    texto="Semana"
                    icono={
                        <CalendarRange
                            size={16}
                        />
                    }
                    onClick={() =>
                        cambiarVista(
                            "semana"
                        )
                    }
                />


                <VistaButton
                    activo={
                        vista === "mes"
                    }
                    texto="Mes"
                    icono={
                        <Calendar1
                            size={16}
                        />
                    }
                    onClick={() =>
                        cambiarVista(
                            "mes"
                        )
                    }
                />


                <VistaButton
                    activo={
                        vista === "año"
                    }
                    texto="Año"
                    icono={
                        <CalendarDays
                            size={16}
                        />
                    }
                    onClick={() =>
                        cambiarVista(
                            "año"
                        )
                    }
                />

            </div>


            {/* =========================
                NAVEGACIÓN
            ========================= */}

            <div
                style={{

                    display:
                        "flex",

                    alignItems:
                        "center",

                    justifyContent:
                        "space-between",

                    background:
                        "#ffffff",

                    border:
                        "1px solid #e2e8f0",

                    borderRadius:
                        18,

                    padding:
                        "10px 12px",

                    marginBottom:
                        18,

                    boxShadow:
                        "0 5px 18px rgba(15,23,42,0.05)"

                }}
            >

                <button
                    type="button"
                    onClick={
                        retroceder
                    }
                    style={
                        botonNavegacion
                    }
                >

                    <ChevronLeft
                        size={21}
                    />

                </button>


                <div
                    style={{

                        textAlign:
                            "center",

                        minWidth:
                            0,

                        padding:
                            "0 8px"

                    }}
                >

                    <div
                        style={{

                            fontSize:
                                12,

                            color:
                                "#64748b",

                            marginBottom:
                                2

                        }}
                    >

                        {
                            obtenerSubtituloNavegacion(
                                vista,
                                fechaActual,
                                hoy
                            )
                        }

                    </div>


                    <div
                        style={{

                            fontSize:
                                16,

                            fontWeight:
                                800,

                            color:
                                "#0f172a",

                            textTransform:
                                "capitalize"

                        }}
                    >

                        {
                            obtenerTituloNavegacion(
                                vista,
                                fechaActual
                            )
                        }

                    </div>

                </div>


                <button
                    type="button"
                    onClick={
                        avanzar
                    }
                    style={
                        botonNavegacion
                    }
                >

                    <ChevronRight
                        size={21}
                    />

                </button>

            </div>


            {/* =========================
                BOTÓN HOY
            ========================= */}

            {
                fechaKey !== hoy &&
                (

                    <button
                        type="button"
                        onClick={
                            irAHoy
                        }
                        style={{

                            width:
                                "100%",

                            border:
                                "none",

                            borderRadius:
                                15,

                            background:
                                "#eff6ff",

                            color:
                                "#2563eb",

                            padding:
                                "11px 14px",

                            fontWeight:
                                700,

                            marginBottom:
                                16

                        }}
                    >

                        Volver a hoy

                    </button>

                )

            }


            {/* =========================
                VISTA HOY
            ========================= */}

            {
                vista === "hoy" && (

                    <VistaDia
                        fecha={
                            fechaActual
                        }
                        ahora={
                            ahora
                        }
                        citas={
                            citas.filter(
                                cita =>
                                    cita.fecha ===
                                    fechaKey
                            )
                        }
                        onSelect={
                            setCitaSeleccionada
                        }
                    />

                )
            }


            {/* =========================
                VISTA SEMANA
            ========================= */}

            {
                vista === "semana" && (

                    <VistaSemana
                        dias={
                            diasSemana
                        }
                        citas={
                            citas
                        }
                        hoy={
                            hoy
                        }
                        onSelect={
                            setCitaSeleccionada
                        }
                        onSelectDay={
                            fecha => {

                                setFechaActual(
                                    fecha
                                );

                                setVista(
                                    "hoy"
                                );

                            }
                        }
                    />

                )
            }


            {/* =========================
                VISTA MES
            ========================= */}

            {
                vista === "mes" && (

                    <VistaMes
                        dias={
                            diasMes
                        }
                        citas={
                            citas
                        }
                        fechaActual={
                            fechaActual
                        }
                        hoy={
                            hoy
                        }
                        onSelect={
                            setCitaSeleccionada
                        }
                        onSelectDay={
                            fecha => {

                                setFechaActual(
                                    fecha
                                );

                                setVista(
                                    "hoy"
                                );

                            }
                        }
                    />

                )
            }


            {/* =========================
                VISTA AÑO
            ========================= */}

            {
                vista === "año" && (

                    <VistaAño
                        año={
                            fechaActual.getFullYear()
                        }
                        citas={
                            citas
                        }
                        onSelectMonth={
                            mes => {

                                setFechaActual(
                                    new Date(
                                        fechaActual.getFullYear(),
                                        mes,
                                        1
                                    )
                                );

                                setVista(
                                    "mes"
                                );

                            }
                        }
                    />

                )
            }


            {/* =========================
                DETALLE
            ========================= */}

            {
                citaSeleccionada && (

                    <DetalleCita
                        cita={
                            citaSeleccionada
                        }
                        onClose={() =>
                            setCitaSeleccionada(
                                null
                            )
                        }
                        onEdit={() =>
                            setMostrarCitaForm(
                                true
                            )
                        }
                    />

                )

            }

            {
                mostrarCitaForm && (

                    <CitaForm
                        paciente={null}
                        cita={
                            citaSeleccionada
                        }
                        onClose={() => {
                            setMostrarCitaForm(
                                false
                            );

                            setCitaSeleccionada(
                                null
                            );
                        }}
                    />

                )
            }

        </div>

    );

}


// =====================================================
// VISTA DEL DÍA
// =====================================================

function VistaDia({
    fecha,
    citas,
    onSelect,
    ahora
}) {

    const citasOrdenadas =
        ordenarCitas(
            citas
        );

    const esHoy =
        convertirFecha(fecha) ===
        convertirFecha(ahora);

    const minutosActuales =
        ahora.getHours() * 60 +
        ahora.getMinutes() +
        ahora.getSeconds() / 60;

    const obtenerMinutosCita = (cita) => {
        const [horas, minutos] = String(
            cita.hora || "00:00"
        )
            .split(":")
            .map(Number);

        return (horas || 0) * 60 + (minutos || 0);
    };

    const marcadorAhora = esHoy ? (
        <div
            key="marcador-ahora"
            className="vd-mobile-now-line"
            aria-label={`Hora actual ${ahora.toLocaleTimeString("es-MX", {
                hour: "2-digit",
                minute: "2-digit",
            })}`}
        >
            <span>
                Ahora · {ahora.toLocaleTimeString("es-MX", {
                    hour: "2-digit",
                    minute: "2-digit",
                })}
            </span>
            <div className="vd-mobile-now-line__rule" />
        </div>
    ) : null;

    let marcadorInsertado = false;


    return (

        <div>

            <div
                style={{

                    marginBottom:
                        12,

                    fontSize:
                        14,

                    fontWeight:
                        700,

                    color:
                        "#475569",

                    textTransform:
                        "capitalize"

                }}
            >

                {
                    formatearFechaLarga(
                        convertirFecha(
                            fecha
                        )
                    )
                }

            </div>


            {
                citasOrdenadas.length === 0 ? (
                    <>
                        {marcadorAhora}
                        <EstadoVacio
                            texto="No hay citas para este día."
                        />
                    </>
                ) : (
                    citasOrdenadas.map(
                        cita => {
                            const mostrarMarcador =
                                esHoy &&
                                !marcadorInsertado &&
                                obtenerMinutosCita(cita) >= minutosActuales;

                            if (mostrarMarcador) {
                                marcadorInsertado = true;
                            }

                            return (
                                <div key={cita.id}>
                                    {mostrarMarcador && marcadorAhora}
                                    <CitaCard
                                        cita={cita}
                                        onClick={() =>
                                            onSelect(
                                                cita
                                            )
                                        }
                                    />
                                </div>
                            );
                        }
                    ).concat(
                        esHoy && !marcadorInsertado
                            ? [marcadorAhora]
                            : []
                    )
                )
            }

        </div>

    );

}


// =====================================================
// VISTA SEMANA
// =====================================================

function VistaSemana({
    dias,
    citas,
    hoy,
    onSelect,
    onSelectDay
}) {

    return (

        <div>

            {
                dias.map(
                    dia => {

                        const key =
                            convertirFecha(
                                dia
                            );


                        const citasDia =
                            ordenarCitas(
                                citas.filter(
                                    cita =>
                                        cita.fecha ===
                                        key
                                )
                            );


                        return (

                            <div
                                key={
                                    key
                                }
                                style={{

                                    background:
                                        "#ffffff",

                                    border:
                                        "1px solid #e2e8f0",

                                    borderRadius:
                                        20,

                                    padding:
                                        14,

                                    marginBottom:
                                        10,

                                    boxShadow:
                                        "0 5px 18px rgba(15,23,42,0.04)"

                                }}
                            >

                                <button
                                    type="button"
                                    onClick={() =>
                                        onSelectDay(
                                            dia
                                        )
                                    }
                                    style={{

                                        width:
                                            "100%",

                                        border:
                                            "none",

                                        background:
                                            "transparent",

                                        padding:
                                            0,

                                        textAlign:
                                            "left",

                                        display:
                                            "flex",

                                        alignItems:
                                            "center",

                                        justifyContent:
                                            "space-between",

                                        marginBottom:
                                            citasDia.length
                                                ? 12
                                                : 0

                                    }}
                                >

                                    <div
                                        style={{

                                            display:
                                                "flex",

                                            alignItems:
                                                "center",

                                            gap:
                                                10

                                        }}
                                    >

                                        <div
                                            style={{

                                                width:
                                                    44,

                                                height:
                                                    44,

                                                borderRadius:
                                                    15,

                                                background:
                                                    key === hoy
                                                        ? "#2563eb"
                                                        : "#eff6ff",

                                                color:
                                                    key === hoy
                                                        ? "#ffffff"
                                                        : "#2563eb",

                                                display:
                                                    "flex",

                                                flexDirection:
                                                    "column",

                                                alignItems:
                                                    "center",

                                                justifyContent:
                                                    "center"

                                            }}
                                        >

                                            <span
                                                style={{

                                                    fontSize:
                                                        11,

                                                    textTransform:
                                                        "uppercase",

                                                    fontWeight:
                                                        700

                                                }}
                                            >

                                                {
                                                    dia.toLocaleDateString(
                                                        "es-MX",
                                                        {
                                                            weekday:
                                                                "short"
                                                        }
                                                    ).replace(
                                                        ".",
                                                        ""
                                                    )
                                                }

                                            </span>


                                            <strong
                                                style={{

                                                    fontSize:
                                                        16

                                                }}
                                            >

                                                {
                                                    dia.getDate()
                                                }

                                            </strong>

                                        </div>


                                        <div>

                                            <div
                                                style={{

                                                    fontWeight:
                                                        800,

                                                    color:
                                                        "#0f172a",

                                                    fontSize:
                                                        15,

                                                    textTransform:
                                                        "capitalize"

                                                }}
                                            >

                                                {
                                                    key === hoy
                                                        ? "Hoy"
                                                        : dia.toLocaleDateString(
                                                            "es-MX",
                                                            {
                                                                weekday:
                                                                    "long"
                                                            }
                                                        )
                                                }

                                            </div>


                                            <div
                                                style={{

                                                    fontSize:
                                                        12,

                                                    color:
                                                        "#64748b",

                                                    marginTop:
                                                        2

                                                }}
                                            >

                                                {
                                                    citasDia.length
                                                }

                                                {
                                                    citasDia.length === 1
                                                        ? " cita"
                                                        : " citas"
                                                }

                                            </div>

                                        </div>

                                    </div>


                                    <ChevronRight
                                        size={19}
                                        color="#94a3b8"
                                    />

                                </button>


                                {
                                    citasDia.map(
                                        cita => (

                                            <CitaCompacta
                                                key={
                                                    cita.id
                                                }
                                                cita={
                                                    cita
                                                }
                                                onClick={() =>
                                                    onSelect(
                                                        cita
                                                    )
                                                }
                                            />

                                        )
                                    )

                                }

                            </div>

                        );

                    }
                )

            }

        </div>

    );

}


// =====================================================
// VISTA MES
// =====================================================

function VistaMes({
    dias,
    citas,
    fechaActual,
    hoy,
    onSelect,
    onSelectDay
}) {

    const citasMes =
        citas.filter(
            cita =>
                cita.fecha &&
                cita.fecha.startsWith(
                    `${fechaActual.getFullYear()}-${String(
                        fechaActual.getMonth() + 1
                    ).padStart(2, "0")}`
                )
        );

    const nombresDias = [
        "L",
        "M",
        "X",
        "J",
        "V",
        "S",
        "D"
    ];


    return (

        <div>

            <div
                style={{

                    background:
                        "#ffffff",

                    border:
                        "1px solid #e2e8f0",

                    borderRadius:
                        22,

                    padding:
                        10,

                    boxShadow:
                        "0 7px 22px rgba(15,23,42,0.05)"

                }}
            >

                <div
                    style={{

                        display:
                            "grid",

                        gridTemplateColumns:
                            "repeat(7, 1fr)",

                        marginBottom:
                            6

                    }}
                >

                    {
                        nombresDias.map(
                            dia => (

                                <div
                                    key={
                                        dia
                                    }
                                    style={{

                                        textAlign:
                                            "center",

                                        fontSize:
                                            11,

                                        fontWeight:
                                            800,

                                        color:
                                            "#64748b",

                                        padding:
                                            "6px 0"

                                    }}
                                >

                                    {
                                        dia
                                    }

                                </div>

                            )
                        )
                    }

                </div>


                <div
                    style={{

                        display:
                            "grid",

                        gridTemplateColumns:
                            "repeat(7, 1fr)",

                        gap:
                            5

                    }}
                >

                    {
                        dias.map(
                            (dia, indice) => {

                                if (
                                    !dia
                                ) {

                                    return (

                                        <div
                                            key={
                                                `empty-${indice}`
                                            }
                                            style={{
                                                minHeight:
                                                    64
                                            }}
                                        />

                                    );

                                }


                                const key =
                                    convertirFecha(
                                        dia
                                    );


                                const citasDia =
                                    citas.filter(
                                        cita =>
                                            cita.fecha ===
                                            key
                                    );


                                const esHoy =
                                    key === hoy;


                                return (

                                    <button
                                        key={
                                            key
                                        }
                                        type="button"
                                        onClick={() =>
                                            onSelectDay(
                                                dia
                                            )
                                        }
                                        style={{

                                            minHeight:
                                                64,

                                            border:
                                                esHoy
                                                    ? "2px solid #2563eb"
                                                    : "1px solid #e2e8f0",

                                            borderRadius:
                                                12,

                                            background:
                                                esHoy
                                                    ? "#eff6ff"
                                                    : "#ffffff",

                                            padding:
                                                6,

                                            textAlign:
                                                "left",

                                            display:
                                                "flex",

                                            flexDirection:
                                                "column",

                                            justifyContent:
                                                "space-between"

                                        }}
                                    >

                                        <span
                                            style={{

                                                fontSize:
                                                    13,

                                                fontWeight:
                                                    esHoy
                                                        ? 800
                                                        : 600,

                                                color:
                                                    esHoy
                                                        ? "#2563eb"
                                                        : "#334155"

                                            }}
                                        >

                                            {
                                                dia.getDate()
                                            }

                                        </span>


                                        {
                                            citasDia.length >
                                                0 && (

                                                <div
                                                    style={{

                                                        display:
                                                            "flex",

                                                        alignItems:
                                                            "center",

                                                        gap:
                                                            4

                                                    }}
                                                >

                                                    <span
                                                        style={{

                                                            width:
                                                                7,

                                                            height:
                                                                7,

                                                            borderRadius:
                                                                "50%",

                                                            background:
                                                                "#2563eb"

                                                        }}
                                                    />


                                                    <span
                                                        style={{

                                                            fontSize:
                                                                10,

                                                            fontWeight:
                                                                700,

                                                            color:
                                                                "#2563eb"

                                                        }}
                                                    >

                                                        {
                                                            citasDia.length
                                                        }

                                                    </span>

                                                </div>

                                            )
                                        }

                                    </button>

                                );

                            }
                        )
                    }

                </div>

            </div>


            <div
                style={{

                    marginTop:
                        14

                }}
            >

                {
                    citasMes.length === 0
                        ? (
                            <EstadoVacio
                                texto="No hay citas en este mes."
                            />
                        )
                        : (
                            citasMes
                                .slice()
                                .sort(
                                    (a, b) =>
                                        obtenerFechaCita(
                                            a
                                        ) -
                                        obtenerFechaCita(
                                            b
                                        )
                                )
                                .map(
                                    cita => (

                                        <CitaCard
                                            key={
                                                cita.id
                                            }
                                            cita={
                                                cita
                                            }
                                            onClick={() =>
                                                onSelect(
                                                    cita
                                                )
                                            }
                                        />

                                    )
                                )
                        )
                }

            </div>

        </div>

    );

}


// =====================================================
// VISTA AÑO
// =====================================================

function VistaAño({
    año,
    citas,
    onSelectMonth
}) {

    const meses =
        Array.from(
            {
                length:
                    12
            },
            (_, mes) =>
                mes
        );


    return (

        <div
            style={{

                display:
                    "grid",

                gridTemplateColumns:
                    "repeat(2, 1fr)",

                gap:
                    10

            }}
        >

            {
                meses.map(
                    mes => {

                        const citasMes =
                            citas.filter(
                                cita =>
                                    obtenerAño(
                                        cita.fecha
                                    ) ===
                                    año &&
                                    obtenerMes(
                                        cita.fecha
                                    ) ===
                                    mes
                            );


                        return (

                            <button
                                key={
                                    mes
                                }
                                type="button"
                                onClick={() =>
                                    onSelectMonth(
                                        mes
                                    )
                                }
                                style={{

                                    border:
                                        "1px solid #e2e8f0",

                                    background:
                                        "#ffffff",

                                    borderRadius:
                                        20,

                                    padding:
                                        16,

                                    minHeight:
                                        130,

                                    textAlign:
                                        "left",

                                    boxShadow:
                                        "0 6px 20px rgba(15,23,42,0.05)"

                                }}
                            >

                                <div
                                    style={{

                                        width:
                                            40,

                                        height:
                                            40,

                                        borderRadius:
                                            14,

                                        background:
                                            "#eff6ff",

                                        display:
                                            "flex",

                                        alignItems:
                                            "center",

                                        justifyContent:
                                            "center",

                                        marginBottom:
                                            12

                                    }}
                                >

                                    <CalendarDays
                                        size={20}
                                        color="#2563eb"
                                    />

                                </div>


                                <div
                                    style={{

                                        fontWeight:
                                            800,

                                        color:
                                            "#0f172a",

                                        fontSize:
                                            15,

                                        textTransform:
                                            "capitalize"

                                    }}
                                >

                                    {
                                        obtenerNombreMes(
                                            mes
                                        )
                                    }

                                </div>


                                <div
                                    style={{

                                        marginTop:
                                            6,

                                        fontSize:
                                            13,

                                        color:
                                            "#64748b"

                                    }}
                                >

                                    {
                                        citasMes.length
                                    }

                                    {
                                        citasMes.length === 1
                                            ? " cita"
                                            : " citas"
                                    }

                                </div>

                            </button>

                        );

                    }
                )

            }

        </div>

    );

}


// =====================================================
// CARD CITA
// =====================================================

function CitaCard({
    cita,
    onClick
}) {

    const esCancelada =
        cita.estado ===
        "cancelada";


    return (

        <button
            type="button"
            onClick={
                onClick
            }
            style={{

                width:
                    "100%",

                textAlign:
                    "left",

                border:
                    "1px solid #e2e8f0",

                background:
                    "#ffffff",

                borderRadius:
                    22,

                padding:
                    16,

                marginBottom:
                    10,

                boxShadow:
                    "0 7px 22px rgba(15,23,42,0.06)",

                display:
                    "block",

                boxSizing:
                    "border-box",

                opacity:
                    esCancelada
                        ? 0.68
                        : 1

            }}
        >

            <div
                style={{

                    display:
                        "flex",

                    alignItems:
                        "flex-start",

                    gap:
                        14

                }}
            >

                <div
                    style={{

                        width:
                            54,

                        height:
                            54,

                        borderRadius:
                            17,

                        background:
                            esCancelada
                                ? "#fef2f2"
                                : "#eff6ff",

                        display:
                            "flex",

                        alignItems:
                            "center",

                        justifyContent:
                            "center",

                        flexShrink:
                            0

                    }}
                >

                    <Clock3
                        size={25}
                        color={
                            esCancelada
                                ? "#ef4444"
                                : "#2563eb"
                        }
                    />

                </div>


                <div
                    style={{

                        flex:
                            1,

                        minWidth:
                            0

                    }}
                >

                    <div
                        style={{

                            display:
                                "flex",

                            alignItems:
                                "center",

                            justifyContent:
                                "space-between",

                            gap:
                                8

                        }}
                    >

                        <div
                            style={{

                                fontSize:
                                    21,

                                fontWeight:
                                    800,

                                color:
                                    "#0f172a"

                            }}
                        >

                            {
                                cita.hora ||
                                "--:--"
                            }

                        </div>


                        {
                            cita.estado && (

                                <EstadoBadge
                                    estado={
                                        cita.estado
                                    }
                                />

                            )
                        }

                    </div>


                    <div
                        style={{

                            display:
                                "flex",

                            alignItems:
                                "center",

                            gap:
                                7,

                            marginTop:
                                7,

                            color:
                                "#334155",

                            fontWeight:
                                600,

                            fontSize:
                                15

                        }}
                    >

                        <UserRound
                            size={17}
                            color="#64748b"
                        />


                        <span
                            style={{

                                overflow:
                                    "hidden",

                                textOverflow:
                                    "ellipsis",

                                whiteSpace:
                                    "nowrap"

                            }}
                        >

                            {
                                cita.pacienteNombre ||
                                "Paciente"
                            }

                        </span>

                    </div>


                    {
                        cita.fecha && (

                            <div
                                style={{

                                    display:
                                        "flex",

                                    alignItems:
                                        "center",

                                    gap:
                                        6,

                                    marginTop:
                                        7,

                                    color:
                                        "#64748b",

                                    fontSize:
                                        12

                                }}
                            >

                                <CalendarDays
                                    size={14}
                                />

                                {
                                    formatearFechaCorta(
                                        cita.fecha
                                    )
                                }

                            </div>

                        )
                    }

                </div>

            </div>

        </button>

    );

}


// =====================================================
// CITA COMPACTA
// =====================================================

function CitaCompacta({
    cita,
    onClick
}) {

    return (

        <button
            type="button"
            onClick={
                onClick
            }
            style={{

                width:
                    "100%",

                border:
                    "none",

                background:
                    "#f8fafc",

                borderRadius:
                    14,

                padding:
                    "10px 11px",

                marginTop:
                    7,

                display:
                    "flex",

                alignItems:
                    "center",

                gap:
                    10,

                textAlign:
                    "left"

            }}
        >

            <div
                style={{

                    fontWeight:
                        800,

                    color:
                        "#2563eb",

                    fontSize:
                        13,

                    minWidth:
                        45

                }}
            >

                {
                    cita.hora ||
                    "--:--"
                }

            </div>


            <div
                style={{

                    flex:
                        1,

                    overflow:
                        "hidden",

                    textOverflow:
                        "ellipsis",

                    whiteSpace:
                        "nowrap",

                    fontSize:
                        13,

                    fontWeight:
                        600,

                    color:
                        "#334155"

                }}
            >

                {
                    cita.pacienteNombre ||
                    "Paciente"
                }

            </div>

        </button>

    );

}


// =====================================================
// DETALLE
// =====================================================

function DetalleCita({
    cita,
    onClose,
    onEdit
}) {

    return (

        <div
            onClick={
                onClose
            }
            style={{

                position:
                    "fixed",

                inset:
                    0,

                background:
                    "rgba(15,23,42,0.45)",

                backdropFilter:
                    "blur(6px)",

                WebkitBackdropFilter:
                    "blur(6px)",

                zIndex:
                    2000,

                display:
                    "flex",

                alignItems:
                    "flex-end",

                justifyContent:
                    "center",

                padding:
                    "12px 12px 108px"

            }}
        >

            <div
                onClick={
                    event =>
                        event.stopPropagation()
                }
                style={{

                    width:
                        "100%",

                    maxWidth:
                        520,

                    background:
                        "#ffffff",

                    borderRadius:
                        "28px 28px 20px 20px",

                    padding:
                        22,

                    boxSizing:
                        "border-box",

                    maxHeight:
                        "calc(100dvh - 120px)",

                    overflowY:
                        "auto",

                    boxShadow:
                        "0 -10px 40px rgba(15,23,42,0.20)"

                }}
            >

                <div
                    style={{

                        display:
                            "flex",

                        alignItems:
                            "center",

                        justifyContent:
                            "space-between",

                        marginBottom:
                            18

                    }}
                >

                    <div>

                        <div
                            style={{

                                fontSize:
                                    12,

                                color:
                                    "#2563eb",

                                fontWeight:
                                    700,

                                textTransform:
                                    "uppercase"

                            }}
                        >

                            Detalle de cita

                        </div>


                        <h2
                            style={{

                                margin:
                                    "4px 0 0",

                                fontSize:
                                    24,

                                color:
                                    "#0f172a"

                            }}
                        >

                            {
                                cita.pacienteNombre ||
                                "Paciente"
                            }

                        </h2>

                    </div>


                    <button
                        type="button"
                        onClick={
                            onClose
                        }
                        style={{

                            width:
                                42,

                            height:
                                42,

                            borderRadius:
                                14,

                            border:
                                "none",

                            background:
                                "#f1f5f9",

                            display:
                                "flex",

                            alignItems:
                                "center",

                            justifyContent:
                                "center"

                        }}
                    >

                        <X
                            size={21}
                            color="#475569"
                        />

                    </button>

                </div>


                <DetalleFila
                    icono={
                        <Clock3
                            size={20}
                            color="#2563eb"
                        />
                    }
                    titulo="Hora"
                    valor={
                        cita.hora ||
                        "Sin hora"
                    }
                />


                <DetalleFila
                    icono={
                        <CalendarDays
                            size={20}
                            color="#2563eb"
                        />
                    }
                    titulo="Fecha"
                    valor={
                        formatearFechaLarga(
                            cita.fecha
                        )
                    }
                />


                {
                    cita.estado && (

                        <DetalleFila
                            icono={
                                <CalendarCheck2
                                    size={20}
                                    color="#2563eb"
                                />
                            }
                            titulo="Estado"
                            valor={
                                cita.estado
                            }
                        />

                    )
                }


                {
                    cita.pacienteNombre && (

                        <DetalleFila
                            icono={
                                <UserRound
                                    size={20}
                                    color="#2563eb"
                                />
                            }
                            titulo="Paciente"
                            valor={
                                cita.pacienteNombre
                            }
                        />

                    )
                }


                {
                    cita.motivo && (

                        <BloqueTexto
                            titulo="Motivo"
                            texto={
                                cita.motivo
                            }
                        />

                    )
                }


                {
                    cita.notas && (

                        <BloqueTexto
                            titulo="Notas"
                            texto={
                                cita.notas
                            }
                        />

                    )
                }

                <button
                    type="button"
                    onClick={
                        onEdit
                    }
                    style={{
                        width: "100%",
                        marginTop: 18,
                        border: "none",
                        borderRadius: 15,
                        background: "#2563eb",
                        color: "#ffffff",
                        padding: "14px 16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                        fontSize: 15,
                        fontWeight: 700
                    }}
                >
                    <Pencil
                        size={18}
                    />

                    Editar cita
                </button>

            </div>

        </div>

    );

}


// =====================================================
// COMPONENTES PEQUEÑOS
// =====================================================

function ResumenCard({
    numero,
    texto
}) {

    return (

        <div
            style={{

                background:
                    "#ffffff",

                border:
                    "1px solid #e2e8f0",

                borderRadius:
                    18,

                padding:
                    "13px 12px",

                boxShadow:
                    "0 5px 18px rgba(15,23,42,0.05)"

            }}
        >

            <div
                style={{

                    fontSize:
                        22,

                    fontWeight:
                        800,

                    color:
                        "#0f172a"

                }}
            >

                {
                    numero
                }

            </div>


            <div
                style={{

                    fontSize:
                        12,

                    color:
                        "#64748b",

                    marginTop:
                        2

                }}
            >

                {
                    texto
                }

            </div>

        </div>

    );

}


function VistaButton({
    activo,
    texto,
    icono,
    onClick
}) {

    return (

        <button
            type="button"
            onClick={
                onClick
            }
            style={{

                border:
                    "none",

                borderRadius:
                    13,

                padding:
                    "9px 4px",

                background:
                    activo
                        ? "#2563eb"
                        : "transparent",

                color:
                    activo
                        ? "#ffffff"
                        : "#64748b",

                fontWeight:
                    700,

                fontSize:
                    11,

                display:
                    "flex",

                flexDirection:
                    "column",

                alignItems:
                    "center",

                justifyContent:
                    "center",

                gap:
                    3

            }}
        >

            {
                icono
            }

            {
                texto
            }

        </button>

    );

}


function EstadoBadge({
    estado
}) {

    let background =
        "#eff6ff";

    let color =
        "#2563eb";


    if (
        estado ===
        "cancelada"
    ) {

        background =
            "#fef2f2";

        color =
            "#dc2626";

    }


    if (
        estado ===
        "confirmada"
    ) {

        background =
            "#f0fdf4";

        color =
            "#16a34a";

    }


    if (
        estado ===
        "pagada"
    ) {

        background =
            "#f0fdf4";

        color =
            "#15803d";

    }


    return (

        <span
            style={{

                background,
                color,

                borderRadius:
                    999,

                padding:
                    "5px 8px",

                fontSize:
                    10,

                fontWeight:
                    700,

                textTransform:
                    "capitalize"

            }}
        >

            {
                estado
            }

        </span>

    );

}


function DetalleFila({
    icono,
    titulo,
    valor
}) {

    return (

        <div
            style={{

                display:
                    "flex",

                alignItems:
                    "center",

                gap:
                    13,

                padding:
                    "13px 0",

                borderBottom:
                    "1px solid #f1f5f9"

            }}
        >

            <div
                style={{

                    width:
                        40,

                    height:
                        40,

                    borderRadius:
                        13,

                    background:
                        "#eff6ff",

                    display:
                        "flex",

                    alignItems:
                        "center",

                    justifyContent:
                        "center",

                    flexShrink:
                        0

                }}
            >

                {
                    icono
                }

            </div>


            <div>

                <div
                    style={{

                        fontSize:
                            12,

                        color:
                            "#64748b",

                        marginBottom:
                            2

                    }}
                >

                    {
                        titulo
                    }

                </div>


                <div
                    style={{

                        fontSize:
                            15,

                        fontWeight:
                            600,

                        color:
                            "#0f172a"

                    }}
                >

                    {
                        valor
                    }

                </div>

            </div>

        </div>

    );

}


function BloqueTexto({
    titulo,
    texto
}) {

    return (

        <div
            style={{

                marginTop:
                    10,

                padding:
                    15,

                background:
                    "#f8fafc",

                borderRadius:
                    16

            }}
        >

            <div
                style={{

                    fontSize:
                        12,

                    color:
                        "#64748b",

                    marginBottom:
                        5

                }}
            >

                {
                    titulo
                }

            </div>


            <div
                style={{

                    color:
                        "#0f172a",

                    lineHeight:
                        1.5,

                    fontSize:
                        14

                }}
            >

                {
                    texto
                }

            </div>

        </div>

    );

}


function EstadoVacio({
    texto
}) {

    return (

        <div
            style={{

                background:
                    "#ffffff",

                borderRadius:
                    24,

                padding:
                    "42px 24px",

                textAlign:
                    "center",

                boxShadow:
                    "0 8px 28px rgba(15,23,42,0.06)"

            }}
        >

            <div
                style={{

                    width:
                        68,

                    height:
                        68,

                    borderRadius:
                        22,

                    background:
                        "#eff6ff",

                    display:
                        "flex",

                    alignItems:
                        "center",

                    justifyContent:
                        "center",

                    margin:
                        "0 auto 16px"

                }}
            >

                <CalendarCheck2
                    size={32}
                    color="#2563eb"
                />

            </div>


            <div
                style={{

                    fontSize:
                        18,

                    fontWeight:
                        700,

                    color:
                        "#0f172a",

                    marginBottom:
                        6

                }}
            >

                Sin citas

            </div>


            <div
                style={{

                    color:
                        "#64748b",

                    fontSize:
                        14

                }}
            >

                {
                    texto
                }

            </div>

        </div>

    );

}


// =====================================================
// HELPERS DE FECHAS
// =====================================================

function normalizarHora(
    hora
) {

    if (
        !hora
    ) {

        return "";

    }


    if (
        typeof hora ===
        "string"
    ) {

        return hora;

    }


    let fecha =
        null;


    if (
        hora instanceof Date
    ) {

        fecha =
            hora;

    }


    if (
        typeof hora.toDate ===
        "function"
    ) {

        fecha =
            hora.toDate();

    }


    if (
        !fecha ||
        Number.isNaN(
            fecha.getTime()
        )
    ) {

        return "";

    }


    const horas =
        String(
            fecha.getHours()
        ).padStart(
            2,
            "0"
        );


    const minutos =
        String(
            fecha.getMinutes()
        ).padStart(
            2,
            "0"
        );


    return `${horas}:${minutos}`;

}

function convertirFecha(
    fecha
) {

    const año =
        fecha.getFullYear();

    const mes =
        String(
            fecha.getMonth() + 1
        ).padStart(
            2,
            "0"
        );

    const dia =
        String(
            fecha.getDate()
        ).padStart(
            2,
            "0"
        );


    return `${año}-${mes}-${dia}`;

}


function obtenerFechaCita(
    cita
) {

    if (
        !cita ||
        !cita.fecha
    ) {

        return 0;

    }


    const hora =
        cita.hora ||
        "00:00";


    return new Date(
        `${cita.fecha}T${hora}`
    ).getTime();

}


function ordenarCitas(
    citas
) {

    return [
        ...citas
    ].sort(
        (a, b) =>
            obtenerFechaCita(
                a
            ) -
            obtenerFechaCita(
                b
            )
    );

}


function obtenerInicioSemana(
    fecha
) {

    const resultado =
        new Date(
            fecha
        );


    const dia =
        resultado.getDay();


    const diferencia =
        dia === 0
            ? -6
            : 1 - dia;


    resultado.setDate(
        resultado.getDate() +
        diferencia
    );


    resultado.setHours(
        0,
        0,
        0,
        0
    );


    return resultado;

}


function construirCalendarioMes(
    fecha
) {

    const año =
        fecha.getFullYear();

    const mes =
        fecha.getMonth();


    const primerDia =
        new Date(
            año,
            mes,
            1
        );


    const ultimoDia =
        new Date(
            año,
            mes + 1,
            0
        );


    const resultado = [];


    let diaSemana =
        primerDia.getDay();


    diaSemana =
        diaSemana === 0
            ? 6
            : diaSemana - 1;


    for (
        let i = 0;
        i < diaSemana;
        i++
    ) {

        resultado.push(
            null
        );

    }


    for (
        let dia = 1;
        dia <= ultimoDia.getDate();
        dia++
    ) {

        resultado.push(
            new Date(
                año,
                mes,
                dia
            )
        );

    }


    return resultado;

}


function formatearFechaLarga(
    fechaString
) {

    if (
        !fechaString
    ) {

        return "Fecha no disponible";

    }


    const fecha =
        new Date(
            `${fechaString}T00:00:00`
        );


    if (
        Number.isNaN(
            fecha.getTime()
        )
    ) {

        return fechaString;

    }


    return fecha.toLocaleDateString(
        "es-MX",
        {
            weekday:
                "long",

            day:
                "numeric",

            month:
                "long",

            year:
                "numeric"
        }
    );

}


function formatearFechaCorta(
    fechaString
) {

    if (
        !fechaString
    ) {

        return "";

    }


    const fecha =
        new Date(
            `${fechaString}T00:00:00`
        );


    return fecha.toLocaleDateString(
        "es-MX",
        {
            day:
                "numeric",

            month:
                "short"
        }
    );

}


function obtenerTituloNavegacion(
    vista,
    fecha
) {

    if (
        vista === "hoy"
    ) {

        return formatearFechaLarga(
            convertirFecha(
                fecha
            )
        );

    }


    if (
        vista === "semana"
    ) {

        const inicio =
            obtenerInicioSemana(
                fecha
            );


        const fin =
            new Date(
                inicio
            );


        fin.setDate(
            fin.getDate() +
            6
        );


        return `${formatearFechaCorta(
            convertirFecha(
                inicio
            )
        )} — ${formatearFechaCorta(
            convertirFecha(
                fin
            )
        )}`;

    }


    if (
        vista === "mes"
    ) {

        return fecha.toLocaleDateString(
            "es-MX",
            {
                month:
                    "long",

                year:
                    "numeric"
            }
        );

    }


    return String(
        fecha.getFullYear()
    );

}


function obtenerSubtituloNavegacion(
    vista,
    fecha,
    hoy
) {

    if (
        vista === "hoy"
    ) {

        return convertirFecha(
            fecha
        ) === hoy
            ? "Hoy"
            : "Día seleccionado";

    }


    if (
        vista === "semana"
    ) {

        return "Semana";

    }


    if (
        vista === "mes"
    ) {

        return "Calendario mensual";

    }


    return "Calendario anual";

}


function obtenerNombreMes(
    mes
) {

    return new Date(
        2024,
        mes,
        1
    ).toLocaleDateString(
        "es-MX",
        {
            month:
                "long"
        }
    );

}


function obtenerAño(
    fecha
) {

    if (
        !fecha
    ) {

        return null;

    }


    return Number(
        fecha.substring(
            0,
            4
        )
    );

}


function obtenerMes(
    fecha
) {

    if (
        !fecha
    ) {

        return null;

    }


    return (
        Number(
            fecha.substring(
                5,
                7
            )
        ) - 1
    );

}


// =====================================================
// ESTILOS
// =====================================================

const botonNavegacion = {

    width:
        40,

    height:
        40,

    borderRadius:
        13,

    border:
        "none",

    background:
        "#eff6ff",

    color:
        "#2563eb",

    display:
        "flex",

    alignItems:
        "center",

    justifyContent:
        "center"

};
